import { AllComponents, WebpageComponentRelation } from "../../../../components/_default/interfaces/base";
import { StructureMap } from "./structureReducer";
import {
  getRelationChildKey,
  normalizeStructureForClient,
  resolveComponentMapKey,
  uniqueByCanonicalKey,
} from "./structureIdentity";
const EDITOR_SENTINEL_ID = -11;

export interface StructureValidationIssue {
  rule: string;
  severity: "repair" | "error";
  message: string;
  componentId?: string | number;
  parentId?: string | number;
  childId?: string | number;
  before?: unknown;
  after?: unknown;
}

export interface StructureValidationSummary {
  repairedRoots: number;
  repairedRelations: number;
  repairedComponents: number;
  removedStaleReferences: number;
  blockingIssues: number;
}

export interface StructureValidationResult {
  structure: StructureMap;
  repairs: StructureValidationIssue[];
  errors: StructureValidationIssue[];
  summary: StructureValidationSummary;
}

type StructureValidationOptions = {
  allowedDetachedRootIds?: Array<string | number>;
};

export class StructureValidationError extends Error {
  result: StructureValidationResult;

  constructor(result: StructureValidationResult) {
    super("Builder structure validation failed.");
    this.name = "StructureValidationError";
    this.result = result;
  }
}

function pushRepair(
  repairs: StructureValidationIssue[],
  summary: StructureValidationSummary,
  issue: Omit<StructureValidationIssue, "severity">
) {
  repairs.push({ ...issue, severity: "repair" });
  if (issue.rule.startsWith("root.")) summary.repairedRoots += 1;
  if (issue.rule.startsWith("relation.")) summary.repairedRelations += 1;
  if (issue.rule.startsWith("component.")) summary.repairedComponents += 1;
  if (issue.rule.includes("stale") || issue.rule.includes("orphan")) {
    summary.removedStaleReferences += 1;
  }
}

function pushError(
  errors: StructureValidationIssue[],
  summary: StructureValidationSummary,
  issue: Omit<StructureValidationIssue, "severity">
) {
  errors.push({ ...issue, severity: "error" });
  summary.blockingIssues += 1;
}

function detectCycle(components: Record<string, AllComponents>): string[] | null {
  const visited = new Set<string>();
  const active = new Set<string>();
  const stack: string[] = [];

  const dfs = (nodeKey: string): string[] | null => {
    if (active.has(nodeKey)) {
      const start = stack.indexOf(nodeKey);
      return start === -1 ? [nodeKey, nodeKey] : [...stack.slice(start), nodeKey];
    }
    if (visited.has(nodeKey)) return null;
    visited.add(nodeKey);
    active.add(nodeKey);
    stack.push(nodeKey);

    const component = components[nodeKey];
    for (const relation of component?.childWebpageComponentRelations ?? []) {
      const childKey = getRelationChildKey(components, relation);
      if (!childKey || !components[childKey]) continue;
      const cycle = dfs(childKey);
      if (cycle) return cycle;
    }

    active.delete(nodeKey);
    stack.pop();
    return null;
  };

  for (const key of Object.keys(components)) {
    const cycle = dfs(key);
    if (cycle) return cycle;
  }
  return null;
}

function normalizeRelationsForComponent(
  componentKey: string,
  component: AllComponents,
  components: Record<string, AllComponents>,
  repairs: StructureValidationIssue[],
  errors: StructureValidationIssue[],
  summary: StructureValidationSummary
): WebpageComponentRelation[] {
  const list = Array.isArray(component.childWebpageComponentRelations)
    ? component.childWebpageComponentRelations
    : [];
  const seen = new Set<string>();
  const out: WebpageComponentRelation[] = [];
  for (const relation of list) {
    const childKey = getRelationChildKey(components, relation);
    if (!childKey || !components[childKey]) {
      pushRepair(repairs, summary, {
        rule: "relation.staleReferenceRemoved",
        message: `Removed stale relation from ${componentKey}.`,
        componentId: componentKey,
      });
      continue;
    }
    if (relation.child_id === EDITOR_SENTINEL_ID) {
      pushRepair(repairs, summary, {
        rule: "relation.editorSentinelRemoved",
        message: `Removed editor sentinel relation from ${componentKey}.`,
        componentId: componentKey,
      });
      continue;
    }
    if (seen.has(childKey)) {
      pushError(errors, summary, {
        rule: "relation.duplicateChild",
        message: `Duplicate child relation detected on ${componentKey}.`,
        componentId: componentKey,
        childId: childKey,
      });
      continue;
    }
    seen.add(childKey);
    const child = components[childKey];
    const normalizedRelation: WebpageComponentRelation = {
      ...relation,
      id: typeof relation.id === "number" ? relation.id : 0,
      parent_id: component.id,
      child_id: child.id,
      sort:
        typeof relation.sort === "number" && Number.isFinite(relation.sort)
          ? relation.sort
          : out.length + 1,
      created_at: relation.created_at ?? null,
      updated_at: relation.updated_at ?? null,
    };
    (normalizedRelation as any).parent_uuid = component.uuid;
    (normalizedRelation as any).child_uuid = child.uuid;
    out.push(normalizedRelation);
  }
  return out.map((relation, index) => ({ ...relation, sort: index + 1 }));
}

export function validateAndRepairStructure(
  structure: StructureMap,
  options: StructureValidationOptions = {}
): StructureValidationResult {
  const repairs: StructureValidationIssue[] = [];
  const errors: StructureValidationIssue[] = [];
  const summary: StructureValidationSummary = {
    repairedRoots: 0,
    repairedRelations: 0,
    repairedComponents: 0,
    removedStaleReferences: 0,
    blockingIssues: 0,
  };

  const normalized = normalizeStructureForClient(structure);
  const canonical = uniqueByCanonicalKey(normalized.components);
  for (const [key, component] of Object.entries(canonical)) {
    if (component.id === EDITOR_SENTINEL_ID || component.type === "editor") {
      delete canonical[key];
      pushRepair(repairs, summary, {
        rule: "component.editorSentinelRemoved",
        message: `Removed editor sentinel component ${key}.`,
        componentId: key,
      });
      continue;
    }
    if (!component.type || typeof component.type !== "string") {
      pushError(errors, summary, {
        rule: "component.missingType",
        message: `Component ${key} is missing a type.`,
        componentId: key,
      });
      continue;
    }
    if (!component.uuid || component.uuid.trim() === "") {
      canonical[key] = { ...component, uuid: key };
      pushRepair(repairs, summary, {
        rule: "component.uuidRepaired",
        message: `Filled missing uuid from map key for ${key}.`,
        componentId: key,
      });
    }
  }

  for (const [key, component] of Object.entries(canonical)) {
    canonical[key] = {
      ...component,
      childWebpageComponentRelations: normalizeRelationsForComponent(
        key,
        component,
        canonical,
        repairs,
        errors,
        summary
      ),
    };
  }

  const rootSeen = new Set<string>();
  const roots: string[] = [];
  for (const rawRoot of normalized.webpageComponents ?? []) {
    const rootKey = resolveComponentMapKey(canonical, rawRoot);
    if (!rootKey) {
      pushRepair(repairs, summary, {
        rule: "root.staleReferenceRemoved",
        message: `Removed stale root reference ${String(rawRoot)}.`,
        before: rawRoot,
      });
      continue;
    }
    if (rootSeen.has(rootKey)) {
      pushRepair(repairs, summary, {
        rule: "root.duplicateRemoved",
        message: `Removed duplicate root ${rootKey}.`,
        componentId: rootKey,
      });
      continue;
    }
    rootSeen.add(rootKey);
    roots.push(rootKey);
  }

  const cycle = detectCycle(canonical);
  if (cycle) {
    pushError(errors, summary, {
      rule: "graph.cycleDetected",
      message: `Cycle detected in builder structure: ${cycle.join(" -> ")}.`,
      before: cycle,
    });
  }

  const reachable = new Set<string>();
  const queue = [...roots];
  while (queue.length > 0) {
    const key = queue.shift()!;
    if (reachable.has(key) || !canonical[key]) continue;
    reachable.add(key);
    for (const relation of canonical[key].childWebpageComponentRelations ?? []) {
      const childKey = getRelationChildKey(canonical, relation);
      if (childKey && canonical[childKey] && !reachable.has(childKey)) {
        queue.push(childKey);
      }
    }
  }

  const allowed = new Set<string>();
  for (const allowedRef of options.allowedDetachedRootIds ?? []) {
    const k = resolveComponentMapKey(canonical, allowedRef);
    if (k) allowed.add(k);
  }

  for (const key of Object.keys(canonical)) {
    if (!reachable.has(key) && !allowed.has(key)) {
      delete canonical[key];
      pushRepair(repairs, summary, {
        rule: "graph.orphanedComponentsRemoved",
        message: `Removed orphaned component ${key}.`,
        componentId: key,
      });
    }
  }

  const finalRoots = roots.filter((root) => canonical[root]);
  const finalComponents = normalizeStructureForClient({
    webpageComponents: finalRoots,
    components: canonical,
  });

  return {
    structure: finalComponents,
    repairs,
    errors,
    summary,
  };
}

export function logStructureValidation(
  action: "save" | "publish",
  result: StructureValidationResult
) {
  if (result.repairs.length === 0 && result.errors.length === 0) return;

  console.groupCollapsed(
    `[builder][${action}] structure validation: ${result.repairs.length} repair(s), ${result.errors.length} error(s)`
  );
  console.log("summary", result.summary);
  for (const repair of result.repairs) console.log("repair", repair);
  for (const error of result.errors) console.error("error", error);
  console.groupEnd();
}
